import { makeAutoObservable, runInAction } from 'mobx';

import AlertAPI from '@/alert/api/AlertAPI';
import { SystemAlert } from '@/alert/entities/SystemAlert';
import useStore from '@/common/hooks/useStore';
import Notifier from '@/common/services/Notifier';
import Storage, { KEY_READ_ALERTS } from '@/common/services/Storage';

export default class AlertStore {
  alerts: SystemAlert[] = [];

  private closingAlerts: string[] = [];

  private readonly alertAPI: AlertAPI;

  constructor() {
    makeAutoObservable<AlertStore, 'closingAlerts' | 'alertAPI'>(this, {
      closingAlerts: false,
      alertAPI: false,
    });

    this.alertAPI = new AlertAPI();
  }

  getAlerts = async () => {
    try {
      const response = await this.alertAPI.getAlerts();

      const idsInStorage = Storage.getObject(KEY_READ_ALERTS, Storage.STORAGE_LOCAL);
      const existedIds = idsInStorage?.split(',') ?? [];

      runInAction(() => {
        this.alerts = response.content
          .filter((alert) => !existedIds.includes(alert.id))
          .map((model) => new SystemAlert(model))
          .slice()
          .sort((a, b) => a.priority - b.priority);
      });
    } catch (e) {
      Notifier.error(e);

      throw e;
    }
  };

  closeAlert = async (id: string) => {
    if (this.closingAlerts.includes(id)) {
      return;
    }

    this.closingAlerts.push(id);

    try {
      await this.alertAPI.readAlert(id);
      await this.getAlerts();
    } catch (e) {
      Notifier.error(e);

      throw e;
    } finally {
      runInAction(() => {
        this.closingAlerts.splice(this.closingAlerts.indexOf(id), 1);
      });
    }
  };

  closeAlertLocally = (id: string) => {
    // TODO use "Storage.pullout"
    const idsInStorage = Storage.getObject(KEY_READ_ALERTS, Storage.STORAGE_LOCAL);

    if (idsInStorage) {
      const existedIds = idsInStorage.split(',');

      existedIds.push(id);

      Storage.saveObject(KEY_READ_ALERTS, existedIds.join(','), Storage.STORAGE_LOCAL);
    } else {
      Storage.saveObject(KEY_READ_ALERTS, id, Storage.STORAGE_LOCAL);
    }

    this.alerts = this.alerts.filter((alert) => alert.id !== id);
  };
}

export const useAlertStore = () => useStore(AlertStore);

export const alertStore = new AlertStore();
